import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Globe, Palette, AlertTriangle, Image as ImageIcon, CreditCard, Sliders } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { storeService } from '../../services/storeService';
import { uploadService } from '../../services/uploadService';
import { defaultTheme } from '../../config/theme';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import MaskedInput from '../../components/common/MaskedInput';
import Skeleton from '../../components/common/Skeleton';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '../../components/common/Tabs';
import DNSInstructionsCard from '../../components/store/DNSInstructionsCard';
import ColorPicker from '../../components/supplier/ColorPicker';
import { unmask } from '../../utils/maskHelpers';
import { loadGoogleFont, preloadGoogleFonts } from '../../utils/loadGoogleFont';

const GOOGLE_FONTS = [
  { value: '', label: 'Padrão (Inter)' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Nunito', label: 'Nunito' },
  { value: 'Source Sans 3', label: 'Source Sans 3' },
  { value: 'Work Sans', label: 'Work Sans' },
  { value: 'Raleway', label: 'Raleway' },
  { value: 'Playfair Display', label: 'Playfair Display' },
];

// Página única `/fornecedor/painel/configuracoes` com 3 tabs:
// - Dados: razão social, CNPJ, telefone, contato
// - Domínio: custom domain + DNS instructions quando não verificado
// - Visual: cores do tema (ColorPicker existente)
const darken = (hex) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const p = 0.15;
  const r = Math.max(0, Math.floor((num >> 16) * (1 - p)));
  const g = Math.max(0, Math.floor(((num >> 8) & 0xff) * (1 - p)));
  const b = Math.max(0, Math.floor((num & 0xff) * (1 - p)));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0').toUpperCase();
};
const lighten = (hex) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const p = 0.15;
  const r = Math.min(255, Math.floor((num >> 16) + (255 - (num >> 16)) * p));
  const g = Math.min(255, Math.floor(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * p));
  const b = Math.min(255, Math.floor((num & 0xff) + (255 - (num & 0xff)) * p));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0').toUpperCase();
};

const normalizeHex = (value) => {
  let v = (value || '').trim();
  if (!v.startsWith('#')) v = '#' + v;
  if (!/^#[0-9A-F]{3,6}$/i.test(v)) return null;
  if (v.length === 4) v = '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
  return v.toUpperCase();
};

const StoreSettings = () => {
  const { currentStore, currentStoreId, refreshProfile } = useAuth();
  const { success, error } = useToast();
  const [tab, setTab] = useState('basics');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [store, setStore] = useState(null);
  const [basics, setBasics] = useState({
    name: '',
    company: '',
    cnpj: '',
    contactPhone: '',
    contactEmail: '',
  });
  const [domain, setDomain] = useState({ customDomain: '' });
  const [payments, setPayments] = useState({
    paymentGateway: 'asaas',
    supportPhone: ''
  });
  // "Parâmetros" — operação da loja (não pagamento). Comissão padrão só faz
  // sentido em loja reseller; UI esconde quando businessType !== 'reseller'.
  const [params, setParams] = useState({
    lowStockThreshold: 5,
    defaultCommissionPercent: ''
  });
  const [theme, setTheme] = useState(defaultTheme);
  const [assets, setAssets] = useState({ logoUrl: '', bannerUrl: '' });
  const [uploadingAsset, setUploadingAsset] = useState(null);

  const load = useCallback(async () => {
    if (!currentStore?.slug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const fresh = await storeService.getBySlug(currentStore.slug);
      setStore(fresh);
      setBasics({
        name: fresh.name || '',
        company: fresh.company || '',
        cnpj: fresh.cnpj || '',
        contactPhone: fresh.contactPhone || '',
        contactEmail: fresh.contactEmail || '',
      });
      setDomain({ customDomain: fresh.customDomain || '' });
      setPayments({
        paymentGateway: fresh.paymentGateway || 'asaas',
        supportPhone: fresh.supportPhone || ''
      });
      setParams({
        lowStockThreshold: Number.isFinite(fresh.lowStockThreshold) ? fresh.lowStockThreshold : 5,
        defaultCommissionPercent: fresh.defaultCommissionPercent != null
          ? String(fresh.defaultCommissionPercent)
          : ''
      });
      setTheme(fresh.theme || defaultTheme);
      setAssets({ logoUrl: fresh.logoUrl || '', bannerUrl: fresh.bannerUrl || '' });
    } catch (err) {
      error(err.message || 'Erro ao carregar loja');
    } finally {
      setLoading(false);
    }
  }, [currentStore, error]);

  useEffect(() => {
    load();
  }, [load]);

  // Pré-carrega todas as opções da lista pra que o preview reflita a fonte
  // imediatamente quando o user alterna no Select.
  useEffect(() => {
    preloadGoogleFonts(GOOGLE_FONTS.map((f) => f.value).filter(Boolean));
  }, []);

  const handleSaveBasics = async () => {
    if (!currentStoreId) return;
    setSaving(true);
    try {
      await storeService.update(currentStoreId, {
        name: basics.name,
        company: basics.company || null,
        cnpj: basics.cnpj ? unmask(basics.cnpj) : null,
        contactPhone: basics.contactPhone ? unmask(basics.contactPhone) : null,
        contactEmail: basics.contactEmail || null,
      });
      success('Dados da loja atualizados!');
      await load();
      if (refreshProfile) refreshProfile();
    } catch (err) {
      error(err.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayments = async () => {
    if (!currentStoreId) return;
    setSaving(true);
    try {
      await storeService.update(currentStoreId, {
        paymentGateway: payments.paymentGateway,
        supportPhone: payments.supportPhone ? unmask(payments.supportPhone) : null
      });
      success('Configurações de pagamento atualizadas.');
      await load();
      if (refreshProfile) refreshProfile();
    } catch (err) {
      error(err.message || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveParams = async () => {
    if (!currentStoreId) return;
    setSaving(true);
    try {
      const payload = {
        lowStockThreshold: Number(params.lowStockThreshold) || 0
      };
      if (isResellerStore) {
        // Vazio limpa o default; número valida 0–100 no backend.
        payload.defaultCommissionPercent = params.defaultCommissionPercent === ''
          ? null
          : Number(params.defaultCommissionPercent);
      }
      await storeService.update(currentStoreId, payload);
      success('Parâmetros atualizados.');
      await load();
      if (refreshProfile) refreshProfile();
    } catch (err) {
      error(err.message || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDomain = async () => {
    if (!currentStoreId) return;
    setSaving(true);
    try {
      await storeService.update(currentStoreId, {
        customDomain: domain.customDomain || null,
      });
      if (domain.customDomain) {
        await storeService.requestDomainVerification(currentStoreId);
      }
      success('Domínio configurado! Siga as instruções DNS abaixo.');
      await load();
    } catch (err) {
      error(err.message || 'Erro ao salvar domínio');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!currentStoreId) return;
    setVerifying(true);
    try {
      const result = await storeService.verifyDomain(currentStoreId);
      if (result?.verified) {
        success('Domínio verificado!');
        await load();
      } else {
        error('Ainda não foi possível verificar. Confirme se o TXT está propagado.');
      }
    } catch (err) {
      error(err.message || 'Erro na verificação');
    } finally {
      setVerifying(false);
    }
  };

  const handleColorChange = (key, value) => {
    const normalized = normalizeHex(value);
    if (!normalized) return;
    setTheme((prev) => {
      const updated = { ...prev, [key]: normalized };
      if (key === 'primary') {
        updated.primaryDark = darken(normalized);
        updated.primaryLight = lighten(normalized);
      }
      if (key === 'secondary') {
        updated.secondaryDark = darken(normalized);
        updated.secondaryLight = lighten(normalized);
      }
      return updated;
    });
  };

  const handleSaveTheme = async () => {
    if (!currentStoreId) return;
    setSaving(true);
    try {
      await storeService.updateTheme(currentStoreId, theme);
      // logoUrl/bannerUrl ficam no body de update da store, não no theme JSON.
      await storeService.update(currentStoreId, {
        logoUrl: assets.logoUrl || null,
        bannerUrl: assets.bannerUrl || null,
      });
      success('Tema atualizado!');
    } catch (err) {
      error(err.message || 'Erro ao salvar tema');
    } finally {
      setSaving(false);
    }
  };

  const handleAssetUpload = async (kind, file) => {
    if (!file) return;
    setUploadingAsset(kind);
    try {
      const { url } = await uploadService.uploadImage(file);
      if (kind === 'logo') setAssets((a) => ({ ...a, logoUrl: url }));
      else if (kind === 'banner') setAssets((a) => ({ ...a, bannerUrl: url }));
      else if (kind === 'favicon') setTheme((t) => ({ ...t, favicon: url }));
      success('Imagem enviada. Lembre de salvar o tema.');
    } catch (err) {
      error(err.message || 'Erro ao enviar imagem');
    } finally {
      setUploadingAsset(null);
    }
  };

  const isCustomPlan = store?.planType === 'CUSTOM';
  const isResellerStore = store?.businessType === 'reseller';

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Skeleton variant="text" width="33%" />
        <Skeleton variant="block" height={300} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-extrabold text-app-text mb-1">Configurações da loja</h1>
      <p className="text-sm text-app-text-secondary mb-6">
        Atualize os dados institucionais, domínio próprio e o visual da sua loja.
      </p>

      <Tabs value={tab} onChange={setTab}>
        <TabList>
          <Tab value="basics">
            <span className="inline-flex items-center gap-1.5">
              <Building2 size={14} /> Dados
            </span>
          </Tab>
          <Tab value="domain">
            <span className="inline-flex items-center gap-1.5">
              <Globe size={14} /> Domínio
            </span>
          </Tab>
          <Tab value="payments">
            <span className="inline-flex items-center gap-1.5">
              <CreditCard size={14} /> Pagamentos
            </span>
          </Tab>
          <Tab value="params">
            <span className="inline-flex items-center gap-1.5">
              <Sliders size={14} /> Parâmetros
            </span>
          </Tab>
          <Tab value="visual">
            <span className="inline-flex items-center gap-1.5">
              <Palette size={14} /> Visual
            </span>
          </Tab>
        </TabList>
        <TabPanels>
          {/* Dados */}
          <TabPanel value="basics">
            <Card className="p-6 space-y-4">
              <Input
                label="Nome da loja (aparece pro cliente)"
                value={basics.name}
                onChange={(e) => setBasics((b) => ({ ...b, name: e.target.value }))}
                required
              />
              <Input
                label="Razão social (opcional)"
                value={basics.company}
                onChange={(e) => setBasics((b) => ({ ...b, company: e.target.value }))}
                placeholder="Nome jurídico completo"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MaskedInput
                  label="CNPJ (opcional)"
                  mask="cnpj"
                  value={basics.cnpj}
                  onChange={(e) => setBasics((b) => ({ ...b, cnpj: e.target.value }))}
                  placeholder="00.000.000/0001-00"
                />
                <MaskedInput
                  label="Telefone"
                  mask="phone"
                  value={basics.contactPhone}
                  onChange={(e) => setBasics((b) => ({ ...b, contactPhone: e.target.value }))}
                  placeholder="(11) 98765-4321"
                />
              </div>
              <Input
                label="Email de contato"
                type="email"
                value={basics.contactEmail}
                onChange={(e) => setBasics((b) => ({ ...b, contactEmail: e.target.value }))}
                placeholder="contato@minhaloja.com"
              />
              <div className="flex justify-end pt-2">
                <Button onClick={handleSaveBasics} loading={saving}>
                  Salvar dados
                </Button>
              </div>
            </Card>
          </TabPanel>

          {/* Domínio */}
          <TabPanel value="domain">
            <Card className="p-6 space-y-4 mb-4">
              <Input
                label="Domínio próprio (opcional)"
                value={domain.customDomain}
                onChange={(e) => setDomain({ customDomain: e.target.value.toLowerCase() })}
                placeholder="minhaloja.com.br"
                helpText="Use só o host (sem http:// ou https://)."
              />
              <div className="flex justify-end">
                <Button onClick={handleSaveDomain} loading={saving}>
                  Salvar domínio
                </Button>
              </div>
            </Card>
            {store?.customDomain && (
              <DNSInstructionsCard store={store} onVerify={handleVerifyDomain} verifying={verifying} />
            )}
            {!store?.customDomain && (
              <Card className="p-4 bg-app-surface border border-app-border">
                <p className="text-sm text-app-text-secondary">
                  Sem um domínio próprio, sua loja fica acessível em{' '}
                  <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-app-border">
                    /fornecedor/{store?.slug}
                  </code>{' '}
                  e no subdomínio{' '}
                  <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-app-border">
                    {store?.slug}.reviter.com
                  </code>.
                </p>
              </Card>
            )}
          </TabPanel>

          {/* Pagamentos */}
          <TabPanel value="payments">
            <Card className="p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-app-text mb-1">Gateway de pagamento</h3>
                <p className="text-xs text-app-text-secondary mb-3">
                  Escolha o adquirente que vai gerar as cobranças desta loja. As credenciais são definidas pelo administrador da plataforma.
                </p>
                <Select
                  value={payments.paymentGateway}
                  onChange={(e) => setPayments((p) => ({ ...p, paymentGateway: e.target.value }))}
                  options={[
                    { value: 'asaas', label: 'Asaas (PIX, boleto, cartão)' },
                    { value: 'pagarme', label: 'Pagar.me (PIX, boleto, cartão)' },
                  ]}
                />
              </div>
              <div className="pt-2 border-t border-app-border">
                <MaskedInput
                  label="Telefone de suporte"
                  mask="phone"
                  value={payments.supportPhone}
                  onChange={(e) => setPayments((p) => ({ ...p, supportPhone: e.target.value }))}
                  placeholder="(11) 98765-4321"
                  helpText="Aparece quando a loja está indisponível."
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={handleSavePayments} loading={saving}>Salvar</Button>
              </div>
            </Card>
          </TabPanel>

          {/* Parâmetros */}
          <TabPanel value="params">
            <Card className="p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-app-text mb-1">Operação da loja</h3>
                <p className="text-xs text-app-text-secondary mb-3">
                  Ajustes operacionais usados pelo backoffice e pelo whitelabel.
                </p>
              </div>
              <Input
                label="Estoque baixo abaixo de"
                type="number"
                min={0}
                value={params.lowStockThreshold}
                onChange={(e) => setParams((p) => ({ ...p, lowStockThreshold: e.target.value }))}
                helpText="Limite para o indicador de estoque baixo. 0 desativa o aviso."
              />
              {isResellerStore && (
                <Input
                  label="Comissão padrão (%)"
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  value={params.defaultCommissionPercent}
                  onChange={(e) => setParams((p) => ({ ...p, defaultCommissionPercent: e.target.value }))}
                  placeholder="Ex.: 15"
                  helpText="Pré-preenche o campo Comissão ao criar variações de produto. Editável caso a caso."
                />
              )}
              <div className="flex justify-end pt-2">
                <Button onClick={handleSaveParams} loading={saving}>Salvar parâmetros</Button>
              </div>
            </Card>
          </TabPanel>

          {/* Visual */}
          <TabPanel value="visual">
            {isCustomPlan && (
              <Card className="p-4 mb-4 bg-warning/5 border-warning/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} className="text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-app-text">
                    Esta loja está no plano <strong>CUSTOM</strong>. As configurações de tema do
                    backoffice <strong>não terão efeito</strong> no whitelabel — o front-end
                    personalizado controla a aparência. Você pode salvar como referência, mas
                    quem renderiza é o front custom.
                  </p>
                </div>
              </Card>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <Card className="p-6">
                  <h3 className="text-lg font-bold text-app-text mb-1">Cores</h3>
                  <p className="text-sm text-app-text-secondary mb-4">
                    A variação escura/clara é calculada automaticamente a partir da cor primária
                    e secundária. Cores seguem o tema de toda a loja (botões, links, destaques).
                  </p>
                  <div className="space-y-4">
                    <ColorPicker
                      label="Primária"
                      value={theme.primary}
                      onChange={(v) => handleColorChange('primary', v)}
                      helpText="Cor principal da marca"
                    />
                    <ColorPicker
                      label="Secundária"
                      value={theme.secondary}
                      onChange={(v) => handleColorChange('secondary', v)}
                      helpText="Complementa a primária"
                    />
                    <ColorPicker
                      label="Destaque"
                      value={theme.accent}
                      onChange={(v) => handleColorChange('accent', v)}
                      helpText="Realces e sucessos"
                    />
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-bold text-app-text mb-1">Imagens</h3>
                  <p className="text-sm text-app-text-secondary mb-4">
                    Logo aparece no cabeçalho, banner na home e favicon na aba do navegador.
                  </p>
                  <div className="space-y-4">
                    <AssetUploader
                      label="Logo"
                      url={assets.logoUrl}
                      onUpload={(file) => handleAssetUpload('logo', file)}
                      onClear={() => setAssets((a) => ({ ...a, logoUrl: '' }))}
                      uploading={uploadingAsset === 'logo'}
                      hint="Recomendado PNG transparente, 200×80px."
                      previewClass="h-16 object-contain"
                    />
                    <AssetUploader
                      label="Banner"
                      url={assets.bannerUrl}
                      onUpload={(file) => handleAssetUpload('banner', file)}
                      onClear={() => setAssets((a) => ({ ...a, bannerUrl: '' }))}
                      uploading={uploadingAsset === 'banner'}
                      hint="Recomendado JPG/WebP, 1920×600px."
                      previewClass="h-24 w-full object-cover"
                    />
                    <AssetUploader
                      label="Favicon"
                      url={theme.favicon}
                      onUpload={(file) => handleAssetUpload('favicon', file)}
                      onClear={() => setTheme((t) => ({ ...t, favicon: null }))}
                      uploading={uploadingAsset === 'favicon'}
                      hint="PNG ou SVG quadrado, 32×32px ou maior."
                      previewClass="h-8 w-8 object-contain"
                    />
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-bold text-app-text mb-1">Tipografia</h3>
                  <p className="text-sm text-app-text-secondary mb-4">
                    Fontes do Google Fonts aplicadas em todo o whitelabel.
                  </p>
                  <Select
                    label="Fonte da loja"
                    value={theme.font || ''}
                    onChange={(e) => {
                      const next = e.target.value || null;
                      if (next) loadGoogleFont(next);
                      setTheme((t) => ({ ...t, font: next }));
                    }}
                    options={GOOGLE_FONTS}
                  />
                  {theme.font && (
                    <p
                      className="mt-3 text-2xl"
                      style={{ fontFamily: `'${theme.font}', sans-serif` }}
                    >
                      Sua loja em {theme.font}
                    </p>
                  )}
                </Card>
              </div>

              <div>
                <Card className="p-6 lg:sticky lg:top-20">
                  <h3 className="text-lg font-bold text-app-text mb-3">Preview</h3>
                  <div className="space-y-3 mb-4">
                    <div
                      className="px-4 py-2.5 rounded-lg text-white font-medium text-sm text-center"
                      style={{
                        background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`,
                      }}
                    >
                      Botão primário
                    </div>
                    <div
                      className="px-4 py-2.5 rounded-lg text-white font-medium text-sm text-center"
                      style={{
                        background: `linear-gradient(135deg, ${theme.secondary}, ${theme.secondaryDark})`,
                      }}
                    >
                      Secundário
                    </div>
                    <div
                      className="px-4 py-2.5 rounded-lg font-medium text-sm text-center"
                      style={{
                        backgroundColor: theme.accent + '20',
                        color: theme.accent,
                      }}
                    >
                      Destaque
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      ['Primária', theme.primary],
                      ['Secundária', theme.secondary],
                      ['Destaque', theme.accent],
                    ].map(([label, hex]) => (
                      <div key={label} className="text-center">
                        <div
                          className="w-full h-10 rounded mb-1 border border-app-border"
                          style={{ backgroundColor: hex }}
                        />
                        <p className="text-xs text-app-text-secondary">{label}</p>
                      </div>
                    ))}
                  </div>
                  <Button fullWidth className="mt-4" onClick={handleSaveTheme} loading={saving}>
                    Salvar tema
                  </Button>
                </Card>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
};

const AssetUploader = ({ label, url, onUpload, onClear, uploading, hint, previewClass }) => {
  const inputId = `asset-${label.toLowerCase()}`;
  return (
    <div>
      <label className="block text-sm font-medium text-app-text mb-1">{label}</label>
      <div className="flex items-center gap-3">
        <div className="w-24 h-16 rounded border border-app-border bg-app-surface flex items-center justify-center overflow-hidden">
          {url ? (
            <img src={url} alt={label} className={previewClass || 'h-full w-full object-contain'} />
          ) : (
            <ImageIcon size={20} className="text-app-text-secondary" />
          )}
        </div>
        <div className="flex-1">
          <input
            id={inputId}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="subtle"
              onClick={() => document.getElementById(inputId).click()}
              loading={uploading}
            >
              {url ? 'Trocar' : 'Enviar'}
            </Button>
            {url && (
              <Button size="sm" variant="ghost" onClick={onClear}>
                Remover
              </Button>
            )}
          </div>
          {hint && <p className="text-xs text-app-text-secondary mt-1">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

export default StoreSettings;
